import { createApp, type Component } from 'vue'
import { createI18n } from 'vue-i18n'
import PrimeVue from 'primevue/config'
import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'
import Toast from 'vue-toastification'
import 'vue-toastification/dist/index.css'
import { MotionPlugin } from '@vueuse/motion';
import { getWebInstrumentations, initializeFaro } from '@grafana/faro-web-sdk'
import { TracingInstrumentation } from '@grafana/faro-web-tracing'
import '@/assets/styles.css'
import * as TablerIcons from '@tabler/icons-vue'
import moment from 'moment';

const faroCollectorUrl = import.meta.env.VITE_FARO_COLLECTOR_URL
const faroEnabled = faroCollectorUrl !== undefined && faroCollectorUrl !== ''

function getOrigins() {
  const pageOrigin = window.location.origin || ''
  const apiOrigin =
    (import.meta.env.VITE_API_ORIGIN as string | undefined) ||
    (import.meta.env.DEV ? 'http://localhost:8000' : pageOrigin)
  return { pageOrigin, apiOrigin }
}

function escapeForRegex(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function buildPropagateUrls(pageOrigin: string, apiOrigin: string) {
  const urls: RegExp[] = []
  if (pageOrigin) {
    urls.push(new RegExp(`^${escapeForRegex(pageOrigin)}/.*`))
  }
  if (apiOrigin && apiOrigin !== pageOrigin) {
    urls.push(new RegExp(`^${escapeForRegex(apiOrigin)}/.*`))
  }
  return urls.length > 0 ? urls : [/.*/]
}

if (faroEnabled) {
  const { pageOrigin, apiOrigin } = getOrigins()
  const propagateUrls = buildPropagateUrls(pageOrigin, apiOrigin)

  initializeFaro({
    url: faroCollectorUrl,
    app: {
      name: 'agents-hub-frontend',
      version: import.meta.env.VITE_APP_VERSION ?? '0.0.0',
    },
    ignoreUrls: [/\/keycloak\//],
    instrumentations: [
      ...getWebInstrumentations(),
      new TracingInstrumentation({
        instrumentationOptions: {
          propagateTraceHeaderCorsUrls: propagateUrls,
        },
      }),
    ],
  })
}

import 'moment/locale/es';
moment.locale('es', {
  monthsShort: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
});

import App from './App.vue'
import router from './router'
import clickOutside from '../../common/src/utils/clickOutside'
import '@/composables/useTheme'

const i18n = createI18n({
  legacy: false,
  locale: navigator.language.split('-')[0],
  fallbackLocale: 'en',
  missingWarn: false,
  fallbackWarn: false
})

const app = createApp(App)
const primeTheme = definePreset(Aura)
for (const [name, component] of Object.entries(TablerIcons)) {
  app.component(name, component as Component)
}
app.use(router)
app.use(i18n)
app.use(PrimeVue, {
  theme: {
    preset: primeTheme,
    options: {
      darkModeSelector: 'html.dark',
    }
  }
})
app.use(Toast, {})
app.use(MotionPlugin)
app.directive('click-outside', clickOutside);
app.mount('#app')
